import { FC, ChangeEvent } from 'react';

import { ProcessingOptions, PipelineStage } from '../../types';

interface ProcessingOptionsProps {
  options: ProcessingOptions;
  onChange: (options: ProcessingOptions) => void;
}

const ProcessingOptionsComponent: FC<ProcessingOptionsProps> = ({ options, onChange }) => {
  const handleStageChange = (stage: PipelineStage): void => {
    const newStages = options.stages.includes(stage)
      ? options.stages.filter((s) => s !== stage)
      : [...options.stages, stage];
    onChange({ ...options, stages: newStages });
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Processing Options</h3>
      <hr className="mb-4 border-gray-200" />

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Pipeline Stages</label>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.stages.includes('stabilization')}
              onChange={() => handleStageChange('stabilization')}
              className="checkbox"
            />
            <span className="text-gray-700">Stabilization (reduce camera shake)</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.stages.includes('denoising')}
              onChange={() => handleStageChange('denoising')}
              className="checkbox"
            />
            <span className="text-gray-700">Denoising (reduce video noise)</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.stages.includes('interpolation')}
              onChange={() => handleStageChange('interpolation')}
              className="checkbox"
            />
            <span className="text-gray-700">Motion Interpolation (increase frame rate)</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.stages.includes('super_resolution')}
              onChange={() => handleStageChange('super_resolution')}
              className="checkbox"
            />
            <span className="text-gray-700">Enhance resolution (up to 2K)</span>
          </label>

          <label className="flex items-center space-x-3 cursor-not-allowed opacity-60">
            <input
              type="checkbox"
              checked={options.stages.includes('color_grading')}
              onChange={() => handleStageChange('color_grading')}
              disabled
              className="checkbox cursor-not-allowed"
            />
            <span className="text-gray-500">
              Color Grading (enhance colors) <em className="text-sm">(Coming Soon)</em>
            </span>
          </label>

          <label className="flex items-center space-x-3 cursor-not-allowed opacity-60">
            <input
              type="checkbox"
              checked={options.stages.includes('packaging')}
              onChange={() => handleStageChange('packaging')}
              disabled
              className="checkbox cursor-not-allowed"
            />
            <span className="text-gray-500">
              Packaging (optimize for web) <em className="text-sm">(Coming Soon)</em>
            </span>
          </label>
        </div>
      </div>

      {options.stages.includes('interpolation') && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Interpolation Backend
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="ffmpeg"
                  checked={options.interpolationBackend === 'ffmpeg'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...options,
                      interpolationBackend: e.target.value as 'ffmpeg' | 'film',
                    })
                  }
                  className="radio"
                />
                <span className="text-gray-700">FFmpeg (faster)</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="film"
                  checked={options.interpolationBackend === 'film'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange({
                      ...options,
                      interpolationBackend: e.target.value as 'ffmpeg' | 'film',
                    })
                  }
                  className="radio"
                />
                <span className="text-gray-700">FILM (higher quality)</span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="label">Target FPS</label>
            <input
              type="number"
              value={options.targetFps}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange({ ...options, targetFps: parseFloat(e.target.value) || 30 })
              }
              className="input"
              placeholder="e.g., 30 or 60"
              min="1"
              max="120"
            />
            <p className="mt-1 text-sm text-gray-500">
              Target frame rate for output video (e.g., 30 or 60)
            </p>
          </div>

          <div className="mb-4">
            <label className="label">Input FPS Override (optional)</label>
            <input
              type="number"
              value={options.inputFpsOverride || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange({
                  ...options,
                  inputFpsOverride: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
              className="input"
              placeholder="Auto-detect"
              min="1"
              max="120"
            />
            <p className="mt-1 text-sm text-gray-500">
              Override input FPS if auto-detection fails (leave empty to auto-detect)
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ProcessingOptionsComponent;
